import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle2, AlertTriangle, User, Receipt, Mail } from 'lucide-react'
import { register as registerApi } from '../../lib/api/auth'
import { useAuth } from '../../store/authStore'
import { PageShell } from '../../components/layout/PageShell'
import { Field, Checkbox } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { Button } from '../../components/ui/Button'
import { COUNTRIES } from '../../lib/countries'
import { BillingFields, billingValid } from '../billing/BillingFields'
import { BILLING_ENABLED } from '../../lib/platform'

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }))
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Form = {
  name: string; lastname: string; email: string; phoneNumber: string; country: string
  billingType: string; codiceFiscale: string; referente: string
  businessName: string; businessVatId: string; businessSDI: string; businessEmail: string; businessPec: string
  businessAddress: string; businessHouseNumber: string; businessCity: string; businessZipCode: string
  businessRegionProvince: string; businessCountry: string; isPublicAdministration: string; commercialPrivacy: string
}

const blank: Form = {
  name: '', lastname: '', email: '', phoneNumber: '', country: 'IT',
  billingType: 'privato', codiceFiscale: '', referente: '',
  businessName: '', businessVatId: '', businessSDI: '', businessEmail: '', businessPec: '',
  businessAddress: '', businessHouseNumber: '', businessCity: '', businessZipCode: '',
  businessRegionProvince: '', businessCountry: 'IT', isPublicAdministration: '', commercialPrivacy: '',
}

export function RegisterPage() {
  const { t } = useTranslation()
  const { locale = 'it' } = useParams()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const status = useAuth((s) => s.status)

  // email may arrive pre-filled from the login screen (unknown email → "Registrati")
  const [form, setForm] = useState<Form>(() => ({ ...blank, email: params.get('email') || '' }))
  // Confirm-email: local only, never sent — guards against typos so the activation email arrives.
  const [confirmEmail, setConfirmEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [msg, setMsg] = useState<'error' | 'exists' | null>(null)

  if (status === 'authenticated') return <Navigate to={`/${locale}/bookings`} replace />

  const emailValid = EMAIL_RE.test(form.email)
  const emailMatch = form.email.trim() === confirmEmail.trim()
  const personalValid = !!form.name.trim() && !!form.lastname.trim() && emailValid && emailMatch
  const valid = personalValid && (!BILLING_ENABLED || billingValid(form))

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMsg(null)
    setForm((p) => ({ ...p, [k]: e.target.value }))
  }
  const setBilling = (k: keyof Form, v: string) => {
    setMsg(null)
    setForm((p) => ({ ...p, [k]: v }))
  }
  const err = (bad: boolean) => (touched && bad ? t('common.required') : undefined)
  const scrollToFirstError = () =>
    setTimeout(() => {
      const el = document.querySelector('[aria-invalid="true"]') as HTMLElement | null
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.focus({ preventScroll: true })
      }
    }, 60)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (!valid) {
      scrollToFirstError()
      return
    }
    setSubmitting(true)
    setMsg(null)
    try {
      const res = await registerApi(form)
      if (res.ok) setDone(true)
      else setMsg(res.reason === 'exists' ? 'exists' : 'error')
    } catch {
      setMsg('error')
    } finally {
      setSubmitting(false)
    }
  }

  if (done)
    return (
      <PageShell narrow>
        <div className="gsm-card p-6 text-center sm:p-8">
          <Mail className="mx-auto h-10 w-10 text-brand-600" />
          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-ink-900">{t('auth.checkEmailTitle')}</h1>
          <p className="mx-auto mt-2 max-w-prose text-sm text-ink-500">
            {t('auth.checkEmailBodyPre')} <span className="font-semibold text-ink-800">{form.email}</span> {t('auth.checkEmailBodyPost')}
          </p>
          <Link to={`/${locale}/login`} className="mt-5 inline-block text-sm font-semibold text-brand-600 hover:text-brand-800">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </PageShell>
    )

  return (
    <PageShell narrow desktopWide>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">{t('auth.registerTitle')}</h1>
        <p className="mt-1.5 text-sm text-ink-500">{t('auth.registerSubtitle')}</p>
      </div>

      <form onSubmit={onSubmit}>
        <fieldset disabled={submitting} className="space-y-5">
          <section className="gsm-card p-5 sm:p-6">
            <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold text-ink-900">
              <User className="h-4 w-4 text-brand-600" /> {t('info.title')}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('info.firstName')} required value={form.name} onChange={set('name')} error={err(!form.name.trim())} autoComplete="given-name" />
              <Field label={t('info.lastName')} required value={form.lastname} onChange={set('lastname')} error={err(!form.lastname.trim())} autoComplete="family-name" />
              <Field label={t('info.email')} required type="email" value={form.email} onChange={set('email')} error={err(!emailValid)} autoComplete="email" />
              <Field
                label={t('info.emailConfirm')}
                required
                type="email"
                value={confirmEmail}
                onChange={(e) => { setMsg(null); setConfirmEmail(e.target.value) }}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                autoComplete="off"
                error={touched && form.email.trim() !== confirmEmail.trim() ? t('info.emailMismatch') : undefined}
              />
              <Field label={t('info.phone')} value={form.phoneNumber} onChange={set('phoneNumber')} autoComplete="tel" />
              <div className="sm:col-span-2">
                <label className="gsm-label">{t('info.country')}</label>
                <Select
                  value={form.country || 'IT'}
                  options={COUNTRY_OPTIONS}
                  onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, country: v, businessCountry: v })) }}
                  searchable
                  ariaLabel={t('info.country')}
                />
              </div>
            </div>
            <p className="mt-3 rounded-xl bg-ink-50 px-3.5 py-2.5 text-[12.5px] text-ink-500">
              {t('auth.passwordHint')}
            </p>
            <div className="mt-3 border-t border-ink-100 pt-3">
              <Checkbox
                checked={form.commercialPrivacy === 'true'}
                onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, commercialPrivacy: v ? 'true' : '' })) }}
                label={t('info.marketing')}
              />
            </div>
          </section>

          {/* billing — TEMPORARILY HIDDEN via BILLING_ENABLED (set VITE_BILLING_ENABLED=true to restore) */}
          {BILLING_ENABLED && (
            <section className="gsm-card p-5 sm:p-6">
              <h2 className="mb-1 flex items-center gap-2 font-display text-sm font-bold text-ink-900">
                <Receipt className="h-4 w-4 text-brand-600" /> {t('profile.billing')}
              </h2>
              <p className="mb-4 text-xs text-ink-400">{t('profile.billingHint')}</p>
              <BillingFields value={form} set={setBilling} touched={touched} requiredLabel={t('common.required')} />
              <div className="mt-4 border-t border-ink-100 pt-3">
                <Checkbox
                  checked={form.isPublicAdministration === 'true'}
                  onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, isPublicAdministration: v ? 'true' : '' })) }}
                  label={t('payment.pa')}
                />
              </div>
            </section>
          )}

          {msg === 'exists' && (
            <p className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-700">
              <AlertTriangle className="h-4 w-4" /> {t('auth.emailExistsPre')}{' '}
              <Link to={`/${locale}/login`} className="underline">{t('auth.login')}</Link>.
            </p>
          )}
          {msg === 'error' && (
            <p className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-700">
              <AlertTriangle className="h-4 w-4" /> {t('auth.genericError')}
            </p>
          )}

          <div className="lg:flex lg:justify-end">
            <Button type="submit" size="lg" className="w-full lg:w-auto lg:px-12" loading={submitting} rightIcon={<CheckCircle2 className="h-4 w-4" />}>
              {t('auth.register')}
            </Button>
          </div>
        </fieldset>
      </form>

      <p className="mt-5 text-center text-sm text-ink-500">
        {t('auth.alreadyAccount')}{' '}
        <button type="button" onClick={() => navigate(`/${locale}/login`)} className="font-semibold text-brand-600 hover:text-brand-800">
          {t('auth.login')}
        </button>
      </p>
    </PageShell>
  )
}
